import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Shuffle, Repeat, Repeat1, ChevronDown, ChevronUp, X,
} from "lucide-react";
import { api } from "./api";
import { mediaUrl, fmtDur } from "./config";

const Ctx = createContext(null);

const RECENT_KEY = "mcs_recent";

export const MusicPlayerProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [muted, setMuted] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("off"); // off | all | one
  const [minimized, setMinimized] = useState(false);
  const audioRef = useRef(null);
  const current = index >= 0 ? queue[index] : null;

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    if (el.dataset.track !== current.id) {
      el.dataset.track = current.id;
      el.src = mediaUrl(current.audioUrl);
      el.play().catch(() => {});
      api.post(`/music/${current.id}/play`).catch(() => {});
      try {
        const ids = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
        localStorage.setItem(RECENT_KEY, JSON.stringify([current.id, ...ids.filter((x) => x !== current.id)].slice(0, 12)));
      } catch {}
    }
  }, [current]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const playTrack = (track, list) => {
    const q = list && list.length ? list : queue.length ? queue : [track];
    const i = q.findIndex((t) => t.id === track.id);
    setQueue(q);
    setIndex(i >= 0 ? i : 0);
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el || !current) return;
    if (playing) el.pause();
    else el.play().catch(() => {});
  };

  const pickNextIndex = (dir) => {
    if (!queue.length) return index;
    if (shuffle) {
      if (queue.length === 1) return index;
      let r = index;
      while (r === index) r = Math.floor(Math.random() * queue.length);
      return r;
    }
    return (index + dir + queue.length) % queue.length;
  };

  const next = () => setIndex(pickNextIndex(1));
  const prev = () => {
    const el = audioRef.current;
    // Spotify behaviour: restart the track if we're a few seconds in, else go to previous.
    if (!shuffle && el && el.currentTime > 3) {
      el.currentTime = 0;
      return;
    }
    setIndex(pickNextIndex(-1));
  };

  const handleEnded = () => {
    if (repeat === "one") {
      const el = audioRef.current;
      if (el) {
        el.currentTime = 0;
        el.play().catch(() => {});
      }
      return;
    }
    if (repeat === "off" && !shuffle && index === queue.length - 1) {
      setPlaying(false);
      return; // stop at the end of the queue
    }
    next();
  };

  const cycleRepeat = () => setRepeat((r) => (r === "off" ? "all" : r === "all" ? "one" : "off"));

  const seek = (frac) => {
    const el = audioRef.current;
    if (el && el.duration) el.currentTime = frac * el.duration;
  };

  const close = () => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute("src");
      el.load();
      delete el.dataset.track;
    }
    setQueue([]);
    setIndex(-1);
    setPlaying(false);
    setProgress(0);
    setMinimized(false);
  };

  return (
    <Ctx.Provider
      value={{
        current, queue, playing, progress, duration, volume, muted,
        shuffle, repeat, minimized,
        setVolume, setMuted, playTrack, toggle, next, prev, seek, close,
        toggleShuffle: () => setShuffle((s) => !s), cycleRepeat,
        toggleMinimized: () => setMinimized((m) => !m),
      }}
    >
      {children}
      <audio
        ref={audioRef}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={handleEnded}
        onTimeUpdate={(e) => setProgress(e.target.duration ? e.target.currentTime / e.target.duration : 0)}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
      />
    </Ctx.Provider>
  );
};

export const usePlayer = () => useContext(Ctx);

const Slider = ({ value, onChange, testId, className = "" }) => (
  <input
    type="range"
    min={0}
    max={100}
    value={Math.round(value * 100)}
    onChange={(e) => onChange(Number(e.target.value) / 100)}
    className={`h-1 cursor-pointer appearance-none rounded-full bg-white/20 ${className}`}
    style={{ accentColor: "#E50914" }}
    data-testid={testId}
  />
);

const iconBtn = "text-slate-400 transition-colors hover:text-white";
const activeIconBtn = "text-crimson transition-colors hover:text-crimson-hover";

export const PlayerBar = () => {
  const {
    current, playing, progress, duration, volume, muted, shuffle, repeat, minimized,
    setVolume, setMuted, toggle, next, prev, seek, close, toggleShuffle, cycleRepeat, toggleMinimized,
  } = usePlayer();
  if (!current) return null;

  const RepeatIcon = repeat === "one" ? Repeat1 : Repeat;

  if (minimized) {
    return (
      <div
        className="fixed bottom-20 right-3 z-40 flex items-center gap-2 rounded-full border border-white/10 bg-[#0c0c12]/95 p-1.5 pr-2 shadow-2xl backdrop-blur-xl lg:bottom-4 lg:right-4"
        data-testid="music-player-mini"
      >
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-surface">
          {mediaUrl(current.artwork) && <img src={mediaUrl(current.artwork)} alt="" className={`h-full w-full object-cover ${playing ? "animate-[spin_6s_linear_infinite]" : ""}`} />}
        </div>
        <button onClick={toggle} className="flex h-9 w-9 items-center justify-center rounded-full bg-crimson text-white transition-transform hover:scale-105" data-testid="mini-toggle">
          {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" fill="white" />}
        </button>
        <button onClick={toggleMinimized} title="Expand player" className={`${iconBtn} p-1`} data-testid="mini-expand">
          <ChevronUp size={17} />
        </button>
        <button onClick={close} title="Close player" className={`${iconBtn} p-1`} data-testid="mini-close">
          <X size={17} />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-14 z-40 border-t border-white/10 bg-[#0c0c12]/95 backdrop-blur-xl lg:bottom-0 lg:left-20" data-testid="music-player-bar">
      <div className="absolute -top-1 left-0 right-0 px-0">
        <Slider value={progress} onChange={seek} testId="player-progress" className="w-full" />
      </div>
      <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-4 py-3 sm:gap-5">
        <div className="flex min-w-0 items-center gap-3 sm:w-56">
          <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-surface">
            {mediaUrl(current.artwork) && <img src={mediaUrl(current.artwork)} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-semibold text-white" data-testid="player-title">{current.title}</p>
            <p className="truncate text-xs text-slate-500">{current.artist}</p>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center gap-2 sm:gap-4">
          <button onClick={toggleShuffle} title="Shuffle" className={shuffle ? activeIconBtn : iconBtn} data-testid="player-shuffle">
            <Shuffle size={17} />
          </button>
          <button onClick={prev} className={iconBtn} data-testid="player-prev">
            <SkipBack size={18} />
          </button>
          <button onClick={toggle} className="flex h-10 w-10 items-center justify-center rounded-full bg-crimson text-white transition-all hover:scale-105 hover:bg-crimson-hover" data-testid="player-toggle">
            {playing ? <Pause size={17} /> : <Play size={17} className="ml-0.5" fill="white" />}
          </button>
          <button onClick={next} className={iconBtn} data-testid="player-next">
            <SkipForward size={18} />
          </button>
          <button onClick={cycleRepeat} title={`Repeat: ${repeat}`} className={repeat !== "off" ? activeIconBtn : iconBtn} data-testid="player-repeat">
            <RepeatIcon size={17} />
          </button>
          <span className="hidden font-mono text-[10px] text-slate-500 sm:inline" data-testid="player-time">
            {fmtDur(progress * duration)} / {fmtDur(duration)}
          </span>
        </div>
        <div className="hidden w-40 items-center gap-2 md:flex">
          <button onClick={() => setMuted((m) => !m)} className={iconBtn} data-testid="player-mute">
            {muted || volume === 0 ? <VolumeX size={17} /> : <Volume2 size={17} />}
          </button>
          <Slider value={muted ? 0 : volume} onChange={(v) => { setMuted(false); setVolume(v); }} testId="player-volume" className="w-full" />
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleMinimized} title="Minimize player" className={`${iconBtn} p-1.5`} data-testid="player-minimize">
            <ChevronDown size={18} />
          </button>
          <button onClick={close} title="Close player" className={`${iconBtn} p-1.5`} data-testid="player-close">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
